import TemplateSurveyState from 'containers/SurveyPage/TemplateSurveyState';
import { find, get, flow, first, cloneDeep } from 'lodash';

import { mapQuestionsAndGroupsAsNodes } from 'containers/QuestionnairePage/utils';
import { transformPropsDeep, escapeHTML } from 'containers/App/utils';
import { Matcher } from 'utils/adl-services';
import {
  SURVEY_TYPE_PEEQ,
  SURVEY_TYPE_DISTRIBUTION_LINK,
} from 'containers/Survey/constants';

import {
  getEmptyAnswer,
  getEmptyNoneOfTheAboveAnswer,
} from 'components/ScreeningQuestion/utils';
import { END_OF_SURVEY } from 'components/LogicPathPreview/constants';
import { FAILURE, REASON_SCREENOUT } from 'components/LogicOutcome/constants';
import { SCREENING_ALLOWED_VALUES } from 'components/ScreeningQuestion/constants';
import {
  screeningQuestionToNormal,
  addScreeningQuestionWithScreeningQL,
  repointCarryForwardFromScreening,
  shouldUseScreeningQl,
  shouldConvertScreeningToQlQuestions,
  applyScreeningQuestionContentUpdate,
} from '../utils';

const onlineRetailerPerceptionsTestingGenerator = ({
  template,
  variables,
  surveyType,
  peeqCategoryType,
}) => {
  // pass the fetchedXXXX options only
  // if you intend to use the relevant template as-is

  // extract and prepare user variables
  const yourBrand = get(find(variables, ['name', 'your_brand']), 'value', {});
  const retailers = get(find(variables, ['name', 'retailers']), 'value', []);
  const period = get(find(variables, ['name', 'period']), 'value', '');
  const defaultRecurrence = get(
    find(variables, ['name', 'defaultRecurrence']),
    'value',
    undefined,
  );

  // replace general texts
  const replacePeriod = (value) =>
    value.replace(/\{\{period\}\}/gi, escapeHTML(period));
  const replaceYourBrand = (value) =>
    value.replace(/\{\{your_brand\}\}/gi, escapeHTML(yourBrand.text));

  const replaceAllTexts = flow([replaceYourBrand, replacePeriod]);

  const questionnaireTemplate = transformPropsDeep(
    template.questionnaireTemplate,
    { prompt: replaceAllTexts, htmlPrompt: replaceAllTexts },
  );

  const { screeningQuestions } = mapQuestionsAndGroupsAsNodes(
    questionnaireTemplate,
  );

  const screeningQuestionClone = cloneDeep(screeningQuestions[0]);
  Object.assign(screeningQuestionClone, {
    answers: [
      {
        ...getEmptyAnswer(),
        body: yourBrand.text,
        media: yourBrand.media,
      },
      ...retailers.map((retailer) => ({
        ...getEmptyAnswer(),
        body: retailer.text,
        media: retailer.media,
        screenerType: SCREENING_ALLOWED_VALUES.NOT_ALLOWED,
      })),
      {
        ...getEmptyAnswer(),
        body: 'Other',
        screenerType: SCREENING_ALLOWED_VALUES.NOT_ALLOWED,
      },
      {
        ...getEmptyNoneOfTheAboveAnswer(),
        screenerType: SCREENING_ALLOWED_VALUES.STRICT_NOT_ALLOWED,
      },
    ],
  });

  const Survey = new TemplateSurveyState({
    fetchedQuestionnaire: {
      ...questionnaireTemplate,
      isPeeq: surveyType === SURVEY_TYPE_PEEQ,
      isDistributionLink: surveyType === SURVEY_TYPE_DISTRIBUTION_LINK,
    },
    fetchedTargeting:
      surveyType === SURVEY_TYPE_PEEQ
        ? template.peeqTargetingTemplate
        : template.regularTargetingTemplate,
  });

  Survey.targeting.updateScreeningQuestion(screeningQuestionClone);
  applyScreeningQuestionContentUpdate(Survey, screeningQuestionClone, {
    surveyType,
    peeqCategoryType,
  });

  if (shouldConvertScreeningToQlQuestions({ surveyType, peeqCategoryType })) {
    // add questions
    // remove from question:isScreening
    // and from answers: screenerType, screening,
    const screenerQuestion = screeningQuestionToNormal(screeningQuestionClone);
    Survey.questionnaire.addQuestion(screenerQuestion);

    repointCarryForwardFromScreening(
      Survey,
      screenerQuestion.refId,
      screeningQuestionClone.refId,
    );

    // move to top of questionnaire
    Survey.questionnaire.moveQuestion(
      screenerQuestion,
      // eslint-disable-next-line no-underscore-dangle
      -(Survey.questionnaire._getQuestionCount() - 1),
    );

    Survey.questionnaire.removeAllScreeningQuestions();

    // set ADL to screenout
    const firstStatementId = Survey.questionnaire.linkAnswerToQuestion({
      question: screenerQuestion,
      answerRefId: first(screenerQuestion.answers).refId,
      targetQuestion: { refId: END_OF_SURVEY },
      context: { questionId: screenerQuestion.refId },
      matcher: Matcher.NotEqual,
    });
    Survey.questionnaire.updateStatementOutcome({
      nodeRefId: screenerQuestion.refId,
      statementId: firstStatementId,
      outcome: {
        params: { id: END_OF_SURVEY },
        type: FAILURE,
        reason: REASON_SCREENOUT,
      },
    });
  } else if (shouldUseScreeningQl({ surveyType, peeqCategoryType })) {
    // add questions
    // remove from question:isScreening
    // and from answers: screenerType, screening,
    const newQLScreener = addScreeningQuestionWithScreeningQL(
      Survey,
      screeningQuestionClone,
    );
    applyScreeningQuestionContentUpdate(Survey, newQLScreener, {
      surveyType,
      peeqCategoryType,
    });

    repointCarryForwardFromScreening(
      Survey,
      newQLScreener.refId,
      screeningQuestionClone.refId,
    );

    Survey.questionnaire.removeQuestion(screeningQuestionClone);
    Survey.targeting.removeAllScreeningQuestion();
  }
  return { state: Survey.state, defaultRecurrence };
};

export default onlineRetailerPerceptionsTestingGenerator;
